import { se } from "date-fns/locale";
import { SerieEntity, SerieType } from "./serie.entity";
import * as _ from "lodash";
import { PointEntity } from "./point.entity";
import { FormatNumbers } from "@arch/_shared/utils";

export class SerieBatchEntity {
    public series: SerieEntity[];
    public source: string;

    private constructor(documents: SerieEntity[]) {
        if (documents.length) {
            this.source = documents?.[0].props?.sources?.[0];
        }
        this.series = documents;
    }

    public static create(series: SerieEntity[]): SerieBatchEntity {
        return new SerieBatchEntity(series);
    }

    public hierarchize(): SerieEntity[] {
        let series: SerieEntity[] = this.series;
        let maxLength = 0;

        series.forEach((serie) => {
            if (serie.props.classifier) {
                const length = serie.props.classifier.length;
                if (length > maxLength) maxLength = length;
                serie.props.level = length - 1;
            }
        });
        series.forEach((serie) => {
            if (serie.props.classifier) {
                const length = serie.props.classifier.length;
                serie.props.classifier += "0".repeat(maxLength - length);
            }
        });
        series = _.sortBy(series, (s) => s.props.classifier);

        series.forEach((serie) => {
            if (serie.props.classifier)
                serie.props.classifier = serie.props.classifier.replace(
                    /[0]+$/,
                    ""
                );
        });

        return series;
    }

    public expandAll(rows: SerieAccountsPlanRowsType[]): void {
        rows.forEach((row) => {
            row.metadata.isExpanded = true;
            row.metadata.isVisible = true;
        });
    }

    public getBudgetRowsTable(options: {
        months: { format: string; date: Date }[];
        originalPoints: PointEntity[];
        secondaryPoints?: PointEntity[];
    }): SerieAccountsPlanRowsType[] {
        const rows: SerieAccountsPlanRowsType[] = [];
        let series = [];

        const generateColumnsPoint = (serie: SerieEntity) => {
            const data: SerieColumnsDateType[] = [];
            for (let month of options.months) {
                const originalPoint = options?.originalPoints.find(
                    (op) =>
                        +op.t === +month.date &&
                        op?.props?.s === serie.props._id
                );
                const budgetPoint = options.secondaryPoints?.find(
                    (bp) =>
                        +bp.t === +month.date &&
                        bp?.props?.s === serie.props._id
                );

                const originalPointValue = serie.getPointValue(originalPoint);
                const budgetPointValue = serie.getPointValue(budgetPoint);

                data.push({
                    dateFormatted: month.format,
                    original: {
                        pointVal: originalPointValue.value,
                        pointValueFormatted: originalPointValue.label
                    },
                    budget: {
                        pointVal: budgetPointValue.value,
                        pointValueFormatted: budgetPointValue.label
                    }
                });
            }

            return data;
        };

        if (this.source === "accounting") {
            series = this.hierarchize();
        } else {
            series = this.organizeClassifiers();
        }

        for (let serie of series) {
            let parentId = null;
            if (this.source !== "accounting") {
                parentId = serie.props.referenceId;
            } else {
                const parentSerie = series.find((s) =>
                    s.props?.compound?.includes(serie?.props?._id)
                );
                parentId = parentSerie?.props?._id;
            }

            let parentMain = null;
            if (serie.level > 2) {
                parentMain = this.findLevelTwoParent(series, parentId);
            }

            rows.push({
                serie,
                metadata: {
                    level: serie.level,
                    ...(serie.props?.highlight?.bold && {
                        bold: serie.props?.highlight?.bold
                    }),
                    hasChildren: !!(serie.props?.compound?.length || 0),
                    isExpanded: [0, 1].includes(serie.level),
                    isVisible: [0, 1, 2].includes(serie.level),
                    parentMain,
                    indentStyle: serie?.indentStyle,
                    parentId: parentId
                },
                columns: generateColumnsPoint(serie)
            });
        }

        return rows;
    }

    public getProjectionRowsTable(options: {
        months: { format: string; date: Date }[];
        points: PointEntity[];
    }): SerieAccountsPlanRowsType[] {
        const rows: SerieAccountsPlanRowsType[] = [];
        let series = [];

        const generateColumnsPoint = (serie: SerieEntity) => {
            const data: SerieColumnsDateType[] = [];
            for (let month of options.months) {
                const originalPoint = options?.points.find(
                    (op) =>
                        +op.t === +month.date &&
                        op?.props?.s === serie.props._id
                );

                const originalPointValue = serie.getPointValue(originalPoint);

                data.push({
                    dateFormatted: month.format,
                    original: {
                        pointVal: originalPointValue.value,
                        pointValueFormatted: originalPointValue.label
                    }
                });
            }

            return data;
        };

        if (this.source === "accounting") {
            series = this.hierarchize();
        } else {
            series = this.organizeClassifiers();
        }

        for (let serie of series) {
            let parentId = null;
            if (this.source !== "accounting") {
                parentId = serie.props.referenceId;
            } else {
                const parentSerie = series.find((s) =>
                    s.props?.compound?.includes(serie?.props?._id)
                );
                parentId = parentSerie?.props?._id;
            }

            let parentMain = null;
            if (serie.level > 2) {
                parentMain = this.findLevelTwoParent(series, parentId);
            }

            rows.push({
                serie,
                metadata: {
                    level: serie.level,
                    ...(serie.props?.highlight?.bold && {
                        bold: serie.props?.highlight?.bold
                    }),
                    hasChildren: !!(serie.props?.compound?.length || 0),
                    isExpanded: [0, 1].includes(serie.level),
                    isVisible: [0, 1, 2].includes(serie.level),
                    parentMain,
                    indentStyle: serie?.indentStyle,
                    parentId: parentId
                },
                columns: generateColumnsPoint(serie)
            });
        }

        return rows;
    }

    private organizeClassifiers(): SerieEntity[] {
        const result = this.series.map((s) => {
            s.props.newClassifier = this.splitterClassifier(s.props.classifier);

            return s;
        });

        const sorted = _.sortBy(result, "props.newClassifier");

        return sorted;
    }

    private splitterClassifier(a: any) {
        if (!a) return "999";

        let splitted = a.split(".");
        let splittedChar = splitted.map((split: any) =>
            String.fromCharCode(split)
        );
        return splittedChar.join(".");
    }

    private findLevelTwoParent(
        series: SerieEntity[],
        parentId: string | null
    ): string | null {
        while (parentId) {
            const parentSerie = series.find((s) => s.props._id === parentId);
            if (!parentSerie) break;

            if (parentSerie.level === 2) {
                return parentSerie.props._id;
            }

            parentId = parentSerie.props.referenceId;
        }

        return null;
    }
}

export type SerieAccountsPlanType = {
    id: string;
    name: string;
    level: number;
    hasChildren: boolean;
    isExpanded: boolean;
    indentStyle?: object;
    bold?: boolean;
    isVisible: boolean;
    parentMain?: string;
    parentId?: string;
    childrenLevel?: number;
    data: {
        [month: string]: {
            original: number;
            budget: number;
        };
    };
};

export type SerieAccountsPlanRowsType = {
    serie: SerieEntity;

    metadata: {
        hasChildren?: boolean;
        isExpanded?: boolean;
        parentMain: string | null;
        level?: number;
        isVisible?: boolean;
        indentStyle?: object;
        parentId?: string;
    };
    columns: SerieColumnsDateType[];
};

export type SerieColumnsDateType = {
    dateFormatted: string;
    original: { pointVal: number; pointValueFormatted: string };
    budget?: { pointVal: number; pointValueFormatted: string };
};
